import axios from 'axios';
import {apiService} from "../../services/api.service";
import {convertToPageable, handleResponse, extractData, handleApiException, downloadFileWithPresignedUrl} from "../../helpers/apiResponseHelper";
import { ElectricityAgreementForm, MeteringPoint, INDIVIDUAL_PACKAGE_VALUE, UpdateElectricityAgreementPackageModalForm, ElectricityAgreementStatus } from './models';
import moment from 'moment';
import { isoDateFormat } from 'common/dateFormatterConfig';
import { normalizeCommaSeperatedEmail } from 'helpers';

async function getElectricityAgreements(pageRequest: any) {
    return axios.get(apiService.getApiUrl() + '/electricity/agreements/admin', {
        params: pageRequest
    }).then(handleResponse).then(convertToPageable).catch(handleApiException);
}

async function getActiveElectricityAgreements(customerId: number) {
    return axios.get(apiService.getApiUrl() + `/electricity/agreements/admin/customer/${customerId}/active`)
        .then(handleResponse).then(extractData);
}

async function createElectricityAgreementOffer(request: ElectricityAgreementForm, meteringPoints: MeteringPoint[]) {
    // TODO: TT-751 uue kliendi lisamine - fields for new customer
    return axios.post(apiService.getApiUrl() + '/electricity/agreements/admin/offer', {
        customerId: request.customerId,
        contactPersonId: request.contactId,
        registryNumber: request.registryNumber,
        companyName: request.customerName,
        email: request.email,
        phone: request.phone,
        address: request.address,
        city: request.city,
        zip: request.zip,
        county: request.county,
        producer: request.sellCheck,
        meteringPoints: meteringPoints,
        energyPackageId: request.energyPackageId,
        consumptionPackage: {
            id: request.consumptionPackage.id !== INDIVIDUAL_PACKAGE_VALUE ? request.consumptionPackage.id : null,
            nameEt: request.consumptionPackage.nameEt,
            nameEn: request.consumptionPackage.nameEn,
            basePriceCentsKwh: request.consumptionPackage.basePriceCentsKwh,
            basePriceTextEt: request.consumptionPackage.basePriceTextEt,
            basePriceTextEn: request.consumptionPackage.basePriceTextEn,
            termsAndConditionsId: request.consumptionPackage.termsAndConditionsId,
            contractTitle: request.consumptionPackage.contractTitle,
            contractPeriodId: request.contractPeriodId,
            pdfFooterDisclaimer: request.consumptionPackage.pdfFooterDisclaimer,
            marketPricePackage: request.consumptionPackage.marketPricePackage,
            pdfProductDescriptionOnInvoice: request.consumptionPackage.pdfProductDescriptionOnInvoice,
            externalProductDescriptionOnInvoice: request.consumptionPackage.externalProductDescriptionOnInvoice,
            jointInvoicePackage: request.isJointInvoicePossible ? request.consumptionPackage.jointInvoicePackage : false,
            dynamicPackage: request.consumptionPackage.dynamicPackage,
            greenEnergy: request.consumptionPackage.greenEnergy,
            baseFixationType: request.consumptionPackage.baseFixationType,
            baseConsumptionProportion: request.consumptionPackage.baseConsumptionProportion,
            twoTariffPackage: request.consumptionPackage.twoTariffPackage,
            dayTariffCentsKwh: request.consumptionPackage.dayTariffCentsKwh,
            nightTariffCentsKwh: request.consumptionPackage.nightTariffCentsKwh,
            monthlyFeeEuros: request.consumptionPackage.monthlyFeeEuros,
            earlyTerminationFeeType: request.consumptionPackage.earlyTerminationFeeType,
            earlyTerminationFee: request.consumptionPackage.earlyTerminationFee,
            meterType: request.consumptionPackage.meterType,
            individual: request.consumptionPackage.id === INDIVIDUAL_PACKAGE_VALUE
        },
        endDate: request.contractPeriodId !== 8 ? null : moment(request.endDate).startOf('day').add(3, 'hours').format(isoDateFormat),
        productionPackageId: request.sellCheck ? request.productionPackageId : null,
        consumptionMarginal: request.consumptionMarginal,
        productionMarginal: request.productionMarginal,
        estimatedConsumptionVolumeMwh: request.estimatedConsumptionVolumeMwh,
        estimatedConsumptionDayVolumeMwh: request.estimatedConsumptionDayVolumeMwh,
        estimatedConsumptionNightVolumeMwh: request.estimatedConsumptionNightVolumeMwh,
        guarantee: request.guarantee,
        consolidatedInvoice: request.consolidatedInvoice,
        firstName: request.firstName,
        lastName: request.lastName,
        personalIdentificationCode: request.contactPersonCode,
        offerDeadline: request.offerDeadline ? moment(request.offerDeadline!).startOf('day').add(3, 'hours').format('YYYY-MM-DD') : null,
        electricityInvoiceRecipientEmail: normalizeCommaSeperatedEmail(request.electricityInvoiceRecipientEmail),
        procurementContract: request.procurementContract,
        saleVendor: request.saleVendor,
        saleNotes: request.saleNotes
    }).then(handleResponse).then(extractData).catch(handleApiException);
}

async function createElectricityAgreement(request: ElectricityAgreementForm, meteringPoints: MeteringPoint[]) {
    // TODO: TT-751 uue kliendi lisamine - fields for new customer
    return axios.post(apiService.getApiUrl() + '/electricity/agreements/admin', {
        customerId: request.customerId,
        contactPersonId: request.contactId,
        registryNumber: request.registryNumber,
        companyName: request.customerName,
        email: request.email,
        phone: request.phone,
        address: request.address,
        city: request.city,
        zip: request.zip,
        county: request.county,
        bankAccountNumber: request.eInvoice ? request.bankAccountNumber : null,
        producer: request.sellCheck,
        meteringPoints: meteringPoints,
        startDate: moment(request.startDate).startOf('day').add(3, 'hours').format(isoDateFormat),
        endDate: request.contractPeriodId !== 8 ? null : moment(request.endDate).startOf('day').add(3, 'hours').format(isoDateFormat),
        energyPackageId: request.energyPackageId,
        //contract period with id 8 is of Tähtajaline
        consumptionPackage: {
            id: request.consumptionPackage.id !== INDIVIDUAL_PACKAGE_VALUE ? request.consumptionPackage.id : null,
            nameEt: request.consumptionPackage.nameEt,
            nameEn: request.consumptionPackage.nameEn,
            basePriceCentsKwh: request.consumptionPackage.basePriceCentsKwh,
            basePriceTextEt: request.consumptionPackage.basePriceTextEt,
            basePriceTextEn: request.consumptionPackage.basePriceTextEn,
            termsAndConditionsId: request.consumptionPackage.termsAndConditionsId,
            contractTitle: request.consumptionPackage.contractTitle,
            contractPeriodId: request.contractPeriodId,
            pdfFooterDisclaimer: request.consumptionPackage.pdfFooterDisclaimer,
            marketPricePackage: request.consumptionPackage.marketPricePackage,
            pdfProductDescriptionOnInvoice: request.consumptionPackage.pdfProductDescriptionOnInvoice,
            externalProductDescriptionOnInvoice: request.consumptionPackage.externalProductDescriptionOnInvoice,   
            jointInvoicePackage: request.isJointInvoicePossible ? request.consumptionPackage.jointInvoicePackage : false,
            dynamicPackage: request.consumptionPackage.dynamicPackage,
            greenEnergy: request.consumptionPackage.greenEnergy,
            baseFixationType: request.consumptionPackage.baseFixationType,
            baseConsumptionProportion: request.consumptionPackage.baseConsumptionProportion,
            twoTariffPackage: request.consumptionPackage.twoTariffPackage,
            dayTariffCentsKwh: request.consumptionPackage.dayTariffCentsKwh,
            nightTariffCentsKwh: request.consumptionPackage.nightTariffCentsKwh,
            monthlyFeeEuros: request.consumptionPackage.monthlyFeeEuros,
            earlyTerminationFeeType: request.consumptionPackage.earlyTerminationFeeType,
            earlyTerminationFee: request.consumptionPackage.earlyTerminationFee,
            meterType: request.consumptionPackage.meterType,
            individual: request.consumptionPackage.id === INDIVIDUAL_PACKAGE_VALUE
        },
        productionPackageId: request.sellCheck ? request.productionPackageId : null,
        consumptionMarginal: request.consumptionMarginal,
        productionMarginal: request.productionMarginal,
        estimatedConsumptionVolumeMwh: request.estimatedConsumptionVolumeMwh,
        estimatedConsumptionDayVolumeMwh: request.estimatedConsumptionDayVolumeMwh,
        estimatedConsumptionNightVolumeMwh: request.estimatedConsumptionNightVolumeMwh,
        guarantee: request.guarantee,
        consolidatedInvoice: request.consolidatedInvoice,
        eInvoice: request.eInvoice,
        firstName: request.firstName,
        lastName: request.lastName,
        personalIdentificationCode: request.contactPersonCode,
        procurementContract: request.procurementContract,
        electricityInvoiceRecipientEmail: normalizeCommaSeperatedEmail(request.electricityInvoiceRecipientEmail)
    }).then(handleResponse).then(extractData).catch(handleApiException);
}

async function getElectricityAgreementFileWithDownload(electricityAgreementId: number, filename: string) {
    return axios.get(`${apiService.getApiUrl()}/electricity/agreements/admin/file?electricityAgreementId=${electricityAgreementId}&downloadAs=${filename}`)
        .then(handleResponse).then(response => downloadFileWithPresignedUrl(response.data));
}

async function getElectricityAgreementFile(electricityAgreementId: number) {
    return axios.get(apiService.getApiUrl() + '/electricity/agreements/admin/file?electricityAgreementId=' + electricityAgreementId)
        .then(handleResponse);
}

async function getElectricityAgreementReportExcel(email: string, withAverageMonthlyConsumption: boolean) {
    return axios.get(apiService.getApiUrl() + '/electricity/agreements/admin/report', {
        params: {
            email,
            withAverageMonthlyConsumption
        }
    });
}

async function getElectricityContractPeriods() {
    return axios.get(apiService.getApiUrl() + '/electricity/contract-period')
        .then(handleResponse).then(extractData);
}

async function changeElectricityAgreementsPackage(agreementId: number, request: UpdateElectricityAgreementPackageModalForm) {
    return axios.post(apiService.getApiUrl() + '/electricity/agreements/admin/change-package/' + agreementId, {
          consumptionPackageId: request.consumptionPackage?.id !== INDIVIDUAL_PACKAGE_VALUE ? request.consumptionPackage?.id : null,
          consumptionMarginal: request.consumptionMarginal,
          productionPackageId: request.productionPackageId,
          productionMarginal: request.productionMarginal,
          nameEt: request.consumptionPackage?.nameEt,
          nameEn: request.consumptionPackage?.nameEn,
          marketPricePackage: request.consumptionPackage?.marketPricePackage,
          pdfProductDescriptionOnInvoice: request.consumptionPackage?.pdfProductDescriptionOnInvoice,
          externalProductDescriptionOnInvoice: request.consumptionPackage?.externalProductDescriptionOnInvoice,
          jointInvoicePackage: request.consumptionPackage?.jointInvoicePackage,
          dynamicPackage: request.consumptionPackage?.dynamicPackage,
          greenEnergy: request.consumptionPackage?.greenEnergy,
          baseFixationType: request.consumptionPackage?.baseFixationType,
          baseConsumptionProportion: request.consumptionPackage?.baseConsumptionProportion,
          twoTariffPackage: request.consumptionPackage?.twoTariffPackage,
          dayTariffCentsKwh: request.consumptionPackage?.dayTariffCentsKwh,
          nightTariffCentsKwh: request.consumptionPackage?.nightTariffCentsKwh,
          basePriceCentsKwh: request.consumptionPackage?.basePriceCentsKwh,
          basePriceTextEt: request.consumptionPackage?.basePriceTextEt,
          basePriceTextEn: request.consumptionPackage?.basePriceTextEn,
          monthlyFeeEuros: request.consumptionPackage?.monthlyFeeEuros,
          earlyTerminationFeeType: request.consumptionPackage?.earlyTerminationFeeType,
          earlyTerminationFee: request.consumptionPackage?.earlyTerminationFee,
          estimatedConsumptionVolumeMwh: request.estimatedConsumptionVolumeMwh,
          estimatedConsumptionDayVolumeMwh: request.estimatedConsumptionDayVolumeMwh,
          estimatedConsumptionNightVolumeMwh: request.estimatedConsumptionNightVolumeMwh,
          termsAndConditionsId: request.consumptionPackage?.termsAndConditionsId,
          contractTitle: request.consumptionPackage?.contractTitle,
          pdfFooterDisclaimer: request.consumptionPackage?.pdfFooterDisclaimer,
          meterType: request.consumptionPackage?.meterType,
          individual: request.consumptionPackage?.id === INDIVIDUAL_PACKAGE_VALUE,
          startDate: moment(request.startDate).startOf('day').add(3, 'hours').format(isoDateFormat)
    }).then(handleResponse).then(extractData);
}

async function cancelElectricityAgreement(agreementId : number) {
    return axios.post(apiService.getApiUrl() + `/electricity/agreements/admin/${agreementId}/cancel`, {})
        .then(handleResponse).then(extractData).catch(handleApiException);
}

interface UpdateElectricityAgreementRequest {
    status: ElectricityAgreementStatus;
    startDate: string;
    endDate?: string;
    consumptionPackageId: number;
    basePriceCentsKwh?: number;
    dayTariffCentsKwh?: number;
    nightTariffCentsKwh?: number;
    monthlyFeeEuros?: number;
    earlyTerminationFee?: number;
    estimatedConsumptionVolumeMwh?: number;
    estimatedConsumptionDayVolumeMwh?: number;
    estimatedConsumptionNightVolumeMwh?: number;
    consumptionMarginalCentsKwh: number;
    productionMarginalCentsKwh?: number;
    invoiceRecipientEmail?: string;
    paidFromCustomersAdvance: boolean;
}

async function updateElectricityAgreement(agreementId: number, request: UpdateElectricityAgreementRequest){
    return axios.patch(apiService.getApiUrl() + `/electricity/agreements/admin/${agreementId}`, 
    {
        ...request
    }).then(handleResponse).then(extractData);
}

export const ElectricityAgreementsService = {
    getElectricityAgreements,
    getElectricityAgreementFileWithDownload,
    getElectricityAgreementFile,
    getElectricityAgreementReportExcel,
    createElectricityAgreementOffer,
    createElectricityAgreement,
    getElectricityContractPeriods,
    changeElectricityAgreementsPackage,
    cancelElectricityAgreement,
    getActiveElectricityAgreements,
    updateElectricityAgreement
};